import { useState, useMemo } from 'react';

import uniqBy from 'lodash/uniqBy';
import { toast } from 'react-toastify';

import { IWallet, useWalletPolicies } from '@entities/wallets';
import { useUpdateWalletPolicyMutation } from '@entities/wallets/api';
import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';
import { ISelectMultiOption, SelectMulti } from '@shared/components/Form';
import { useModal } from '@shared/components/Modal';

interface IProps {
  selectedIds: string[];
  wallets?: IWallet[];
  onSuccess?: () => void;
}

export const ChangeWalletPolicyModal = ({ selectedIds, wallets, onSuccess }: IProps) => {
  const { closeModal } = useModal();
  const { walletPolicies } = useWalletPolicies();
  const [updateWalletPolicy, { isLoading }] = useUpdateWalletPolicyMutation();

  const [selectedPolicyName, setSelectedPolicyName] = useState<string>('');
  const [selectedMerchants, setSelectedMerchants] = useState<ISelectMultiOption[]>([]);

  const selectedPolicy = useMemo(() => {
    return walletPolicies?.data?.find(p => p.wallet_policy.name === selectedPolicyName);
  }, [walletPolicies, selectedPolicyName]);

  const policyOptions = useMemo(() => {
    return walletPolicies?.data?.map(p => p.wallet_policy) || [];
  }, [walletPolicies]);

  const handleApply = async () => {
    if (!selectedPolicyName) return;
    try {
      await updateWalletPolicy({
        parent_wallet_ids: selectedIds,
        policy_name: selectedPolicyName,
        traffic: selectedPolicy?.traffic || 'std',
        exclude_merchants_ids: selectedMerchants.map(merchant => merchant.value),
      }).unwrap();
      toast.success('Bulk change wallet policy successfully');
      onSuccess?.();
      closeModal();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Bulk update failed', e);
    }
  };

  const handleChange = (event: SelectChangeEvent) => {
    setSelectedPolicyName(event.target.value as string);
  };

  const merchantExceptions = useMemo(() => {
    return wallets
      .filter(wallet => wallet.merchant_id !== null)
      .map(wallet => {
        return {
          value: wallet.merchant_id,
          label: wallet.merchant_short_name,
        };
      });
  }, [wallets]);

  return (
    <Box
      sx={{ p: 2, minWidth: 400, height: 500, display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <Typography variant='h6'>Please select Properties</Typography>

      <Box>
        <Typography variant='caption' sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
          Wallet Policy
        </Typography>
        <FormControl fullWidth size='small'>
          <Select
            value={selectedPolicyName}
            onChange={handleChange}
            displayEmpty
            renderValue={selected => {
              if (selected.length === 0) {
                return <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select Policy</em>;
              }
              return selected;
            }}
          >
            {policyOptions.map(option => (
              <MenuItem key={option.name} value={option.name}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedPolicy && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant='body2' color='text.secondary'>
                Traffic Type
              </Typography>
              <Typography variant='body2' fontWeight={500}>
                {selectedPolicy.traffic}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant='body2' color='text.secondary'>
                Traffic Limit
              </Typography>
              <Typography variant='body2' fontWeight={500}>
                {selectedPolicy.min} - {selectedPolicy.max}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant='caption' sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
          Merchants Exception
        </Typography>
        <SelectMulti
          name='exclude_merchants_ids'
          options={uniqBy(merchantExceptions, 'value')?.map(merchant => ({
            value: merchant.value,
            label: merchant.label,
          }))}
          onChange={value => {
            setSelectedMerchants(value as ISelectMultiOption[]);
          }}
          value={selectedMerchants}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
        <Button variant='outlined' color='error' onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleApply}
          disabled={!selectedPolicyName || isLoading}
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
};
